import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { AlertDialog, LoadingComponent, PageStatus } from "ndr-designsystem"
import { Info, UserRound, Phone, ShieldCheck, Users as UsersIcon, RefreshCw, LogIn, Power, Wrench } from "lucide-react";
import {
    DetailsViewRoot,
    DetailsHeader,
    DetailsTitle,
    ContentWithSidebar,
    DetailsContent,
    StyledStatusBadge,
    EditButton,
    ConfirmButton,
    DiscardButton,
    SidebarRoot,
    SectionContainer,
    SectionTitle,
    SectionDivider,
    NavButton,
    ValidationDot,
} from "../../../Components/detailsView/DetailsView.styled";
import { CertificateType } from "../../../api/fixed/Certificate"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { AddTenant, editTenantActivation, editTenantDetails, fetchCertificate, fetchTenantByID, repairTenant } from "../store/Thunks";
import { setCertificates, setCurrentTenant } from "../store/TenantsSlice"
import Tenant from "../../../api/fixed/Tenant";
import MainContact from "./MainContact";
import GeneralTab from "./GeneralTab";
import OnCallTab from "./OnCallTab";
import {
    GeneralTabSchema,
    MainContactSchemaFromTenant,
    OnCallTabSchema
} from "../../../validations/TenantDetailsValidation";
import Certificates from "./Certificates";
import Users from "./Users";
import { setCurrentPage, setNotificationAlertMessage } from "../../appStateSlice";
import { isUserSuperAdmin } from "../../Auth/Thunks";

interface RouteParams {
    id: string
    page: string
}

type TenantTabKey = "general" | "main-contact" | "contact" | "certificates" | "users";

const EDITING_TABS: TenantTabKey[] = ["general", "main-contact", "contact"];

interface SidebarItem {
    key: TenantTabKey;
    label: string;
    icon: React.ElementType;
    isValid?: boolean;
}

const TenantDetails: React.FC<{}> = () => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const { id, page } = useParams<RouteParams>();
    const [pageStatus, setPageStatus] = useState<PageStatus>(PageStatus.VIEW);
    const [isSaving, setIsSaving] = useState(false);
    const [isRepairing, setIsRepairing] = useState(false);
    const isEditing = pageStatus === PageStatus.EDIT;
    const isAdding = pageStatus === PageStatus.ADD;
    const [alertOpen, setAlertOpen] = useState(false);
    const [loginURL, setLoginURL] = useState<string>();
    const { currentTenant: tenant } = useAppSelector(state => state.Tenants)
    const { userRole } = useAppSelector(state => state.appState)
    const isSuperAdmin = isUserSuperAdmin(userRole);
    const { t } = useTranslation(['tabels', 'common']);

    const history = useHistory();
    const generalTabSchema = GeneralTabSchema(isAdding);
    const mainContactSchema = MainContactSchemaFromTenant(isAdding);
    const onCallTabSchema = OnCallTabSchema(isAdding);

    const isGeneralTabValid = generalTabSchema.isValidSync(tenant);
    const isMainContactSchemaValid = mainContactSchema.isValidSync(tenant);
    const isOnCallSchemaValid = onCallTabSchema.isValidSync(tenant);
    // The server requires on-call data even when AVAT is used, so always validate it while adding
    const isAllValid = isGeneralTabValid && isMainContactSchemaValid
        && ((!isAdding && tenant?.useAvatForActivations) || isOnCallSchemaValid);

    useEffect(() => {
        if (id !== "add") {
            dispatch(fetchTenantByID(id));
        } else {
            dispatch(setCurrentTenant({}));
            setPageStatus(PageStatus.ADD);
        }
        return () => dispatch(setCurrentTenant(undefined))

    }, [dispatch, id])

    useEffect(() => {
        if (id !== "add") {
            dispatch(fetchCertificate({ tenantID: id, CT: CertificateType.decryption }))
            dispatch(fetchCertificate({ tenantID: id, CT: CertificateType.encryption }))
            dispatch(fetchCertificate({ tenantID: id, CT: CertificateType.auth }))
        }
        return () => dispatch(setCertificates({
            encryption: "none",
            decryption: "none",
            auth: "none"
        }))
    }, [dispatch, id])

    useEffect(() => {
        if (id !== "add" && tenant?.subdomain) {
            setLoginURL([`${window.location.protocol}//${tenant.subdomain}.ui`, ...window.location.hostname.split('.').splice(1)].join('.'))
        }
        return () => {
            setLoginURL(undefined)
        }
    }, [id, tenant?.subdomain])

    useEffect(() => {
        if (pageStatus === PageStatus.EDIT)
            dispatch(setCurrentPage("edit_tenant"));
        else if (pageStatus === PageStatus.ADD)
            dispatch(setCurrentPage("add_tenant"))
        else
            dispatch(setCurrentPage("tenant_details"))
    }, [dispatch, pageStatus])

    const stammdatenItems: SidebarItem[] = useMemo(() => {
        const items: SidebarItem[] = [
            { key: "general", label: t("tenantDetails.tabs.general"), icon: Info, isValid: isGeneralTabValid },
            { key: "main-contact", label: t("tenantDetails.tabs.mainContact"), icon: UserRound, isValid: isMainContactSchemaValid },
        ];
        // While adding, the on-call tab must stay visible: the server requires its fields
        if (isAdding || !tenant?.useAvatForActivations)
            items.push({ key: "contact", label: t("tenantDetails.tabs.onCall"), icon: Phone, isValid: isOnCallSchemaValid });
        return items;
    }, [t, tenant?.useAvatForActivations, isAdding, isGeneralTabValid, isMainContactSchemaValid, isOnCallSchemaValid]);

    const managementItems: SidebarItem[] = useMemo(() => {
        if (isAdding || isEditing) return [];
        return [
            { key: "certificates", label: t("tenantDetails.tabs.certificates"), icon: ShieldCheck },
            { key: "users", label: t("tenantDetails.tabs.users"), icon: UsersIcon },
        ];
    }, [t, isAdding, isEditing]);

    const visibleKeys = useMemo(
        () => [...stammdatenItems, ...managementItems].map(i => i.key),
        [stammdatenItems, managementItems],
    );

    const selectedTab: TenantTabKey = visibleKeys.includes(page as TenantTabKey) ? page as TenantTabKey : "general";

    const handleTenantAdd = async (): Promise<void> => {
        setIsSaving(true);
        try {
            const result: Tenant = await dispatch(AddTenant(tenant!)).unwrap()
            if (result.inventoryItemId) {
                history.replace(`/tenants/${result.inventoryItemId}`)
                setPageStatus(PageStatus.VIEW)
            }
        } catch {
            // Error snackbar is dispatched globally by the API client;
            // stay in ADD mode so the user can correct the data.
        } finally {
            setIsSaving(false);
        }
    }

    const handleRefresh = async (): Promise<void> => {
        await dispatch(fetchTenantByID(id));
    }

    const handleTenantEdit = async (): Promise<void> => {
        setIsSaving(true);
        try {
            await dispatch(editTenantDetails(tenant!)).unwrap();
            await dispatch(fetchTenantByID(id));
            setPageStatus(PageStatus.VIEW)
        } catch {
            // Error snackbar is dispatched globally by the API client;
            // stay in EDIT mode so the user can correct the data.
        } finally {
            setIsSaving(false);
        }
    }

    const handleActive = async (): Promise<void> => {
        if (tenant) {
            await dispatch(editTenantActivation({ id: tenant.inventoryItemId!, revision: tenant.revision!, active: !tenant.isActive }));
            await dispatch(fetchTenantByID(id));
        }
    }

    const handleRepair = async (): Promise<void> => {
        if (tenant?.inventoryItemId) {
            setIsRepairing(true);
            try {
                await dispatch(repairTenant(tenant.inventoryItemId)).unwrap();
                dispatch(setNotificationAlertMessage(t('tenantDetails.repairSuccess')));
            } catch {
                // Global error toast handles failure
            } finally {
                setIsRepairing(false);
            }
        }
    }

    const handleEditMode = (): void => {
        if (pageStatus !== PageStatus.EDIT)
            setPageStatus(PageStatus.EDIT);
        else
            setAlertOpen(true)
    }

    const onAlertAgree = (): void => {
        setPageStatus(PageStatus.VIEW)
        dispatch(fetchTenantByID(id));
        setAlertOpen(false)
    }

    const onAlertCancel = (): void => {
        setAlertOpen(false)
    }

    const selectTab = (newTab: TenantTabKey): void => {
        if (newTab === "general")
            history.replace(`/tenants/${id}`)
        else
            history.replace(`/tenants/${id}/${newTab}`)
    };

    const renderNavItems = (items: SidebarItem[]): React.ReactNode => (
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
            {items.map(item => (
                <li key={item.key}>
                    <NavButton isSelected={selectedTab === item.key} onClick={() => selectTab(item.key)}>
                        <item.icon
                            size={18}
                            color={selectedTab === item.key ? theme.palette.primary.main : theme.slate[400]}
                        />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                            {item.label}
                        </span>
                        {item.isValid !== undefined && <ValidationDot isValid={item.isValid} />}
                    </NavButton>
                </li>
            ))}
        </Box>
    );

    const renderTabContent = (): React.ReactNode => {
        if (isSaving)
            return <LoadingComponent />

        switch (selectedTab) {
            case "main-contact":
                return <MainContact pageStatus={pageStatus} />
            case "contact":
                return <OnCallTab pageStatus={pageStatus} />
            case "certificates":
                return <Certificates />
            case "users":
                return <Users />
            case "general":
            default:
                return <GeneralTab pageStatus={pageStatus} />
        }
    };

    if (tenant === undefined) return <LoadingComponent />

    return (
        <DetailsViewRoot style={{ pointerEvents: isSaving ? "none" : "all" }}>
            <DetailsHeader>
                <DetailsTitle>
                    {isAdding ? t("tenantDetails.tabs.general") : tenant?.name ?? "—"}
                    {!isAdding &&
                        <StyledStatusBadge isActive={tenant?.isActive}>
                            {tenant?.isActive ? t("ACTIVE") : t("tenantDetails.deactivate")}
                        </StyledStatusBadge>
                    }
                </DetailsTitle>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {pageStatus === PageStatus.VIEW &&
                        <>
                            <Tooltip title={t("common:refresh", "Refresh") as string}>
                                <IconButton size="small" color="primary" onClick={handleRefresh}>
                                    <RefreshCw size={16} />
                                </IconButton>
                            </Tooltip>
                            {EDITING_TABS.includes(selectedTab) &&
                                <EditButton onClick={handleEditMode}>
                                    {t("tenantDetails.edit")}
                                </EditButton>
                            }
                            {tenant?.isActive && loginURL &&
                                <DiscardButton startIcon={<LogIn size={14} />}
                                               onClick={() => { window.location.href = loginURL }}>
                                    {t("common:LOGIN")}
                                </DiscardButton>
                            }
                            <DiscardButton startIcon={<Power size={14} />} onClick={handleActive}>
                                {tenant?.isActive ? t('tenantDetails.deactivate') : t('tenantDetails.activate')}
                            </DiscardButton>
                            {isSuperAdmin &&
                                <DiscardButton
                                    startIcon={<Wrench size={14} />}
                                    onClick={handleRepair}
                                    disabled={isRepairing}
                                >
                                    {t('tenantDetails.repair')}
                                </DiscardButton>
                            }
                        </>
                    }
                    {pageStatus === PageStatus.EDIT &&
                        <>
                            <DiscardButton onClick={handleEditMode}>
                                {t("tenantDetails.cancel")}
                            </DiscardButton>
                            <ConfirmButton disabled={!isAllValid} onClick={handleTenantEdit}>
                                {t("tenantDetails.submit")}
                            </ConfirmButton>
                        </>
                    }
                    {pageStatus === PageStatus.ADD &&
                        <ConfirmButton disabled={!isAllValid} onClick={handleTenantAdd}>
                            {t("tenantDetails.submit")}
                        </ConfirmButton>
                    }
                </Box>
            </DetailsHeader>

            <AlertDialog text={t("common:cancel_edit_mode")} onAgree={onAlertAgree} alertOpen={alertOpen}
                         onCancel={onAlertCancel} cancelText={t("common:Disagree")} acceptText={t("common:Agree")} />

            <ContentWithSidebar>
                <SidebarRoot>
                    <SectionContainer>
                        <SectionTitle sx={{ mb: 0.5 }}>{t("tenantDetails.tabs.general")}</SectionTitle>
                        {renderNavItems(stammdatenItems)}
                    </SectionContainer>
                    {managementItems.length > 0 &&
                        <>
                            <SectionDivider />
                            <SectionContainer>
                                {renderNavItems(managementItems)}
                            </SectionContainer>
                        </>
                    }
                </SidebarRoot>
                <DetailsContent>
                    {renderTabContent()}
                </DetailsContent>
            </ContentWithSidebar>
        </DetailsViewRoot>
    )
}

export default TenantDetails
